<template>
  <v-card flat>
    <v-dialog max-width="600px" v-model="showAddPostcode">
      <v-card>
        <v-card-title>Add Postcode to Region</v-card-title>
        <v-card-text>
          <v-text-field
            label="Postcode"
            v-model="newPostcode.postcode"
            placeholder="Enter postcode"
          />
          
          <v-radio-group v-model="useExistingRegion" row class="mt-0">
            <v-radio label="Use existing region" :value="true"></v-radio>
            <v-radio label="Create new region" :value="false"></v-radio>
          </v-radio-group>
          
          <template v-if="useExistingRegion">
            <v-select
              label="Region"
              v-model="newPostcode.region"
              :items="regionOptions"
              item-text="text"
              item-value="value"
              placeholder="Select region"
            />
          </template>
          
          <template v-else>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="City"
                  v-model="newRegion.city"
                  placeholder="Enter city"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="Province"
                  v-model="newRegion.province"
                  placeholder="Enter province code (e.g. BC, ON)"
                  @input="newRegion.province = $event ? $event.toUpperCase() : ''"
                />
              </v-col>
            </v-row>
            <p class="text-caption grey--text">Area will be set to 'local' by default</p>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="showAddPostcode = false">Cancel</v-btn>
          <v-spacer/>
          <v-btn color="success" @click="addPostcode" :disabled="!isValidNewPostcode">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog max-width="600px" v-model="showPostcodeCheck">
      <v-card>
        <v-card-title>Check Postcode</v-card-title>
        <v-card-text>
          <v-text-field
            label="Postcode"
            v-model="postcodeToCheck"
            placeholder="Enter postcode to check"
            @keyup.enter="checkPostcode"
          />
          <div v-if="postcodeCheckResult" class="mt-4">
            <v-alert :type="postcodeCheckResult.valid ? 'success' : 'error'" text>
              <strong>{{ postcodeCheckResult.postcode }}</strong> is 
              <span v-if="postcodeCheckResult.valid">
                valid for region: <strong>
                {{ postcodeCheckResult.area }}
                {{ postcodeCheckResult.city }}
                {{ postcodeCheckResult.province }}
              </strong>

              </span>
              <span v-else>not valid<span v-if="postcodeCheckResult.error"> ({{ postcodeCheckResult.error }})</span></span>
            </v-alert>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="showPostcodeCheck = false">Close</v-btn>
          <v-spacer/>
          <v-btn color="primary" @click="checkPostcode" :disabled="!postcodeToCheck">Check</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog max-width="800px" v-model="showBulkImport">
      <v-card>
        <v-card-title>Bulk Import Postcodes</v-card-title>
        <v-card-text>
          <v-radio-group v-model="bulkUseExistingRegion" row class="mt-0">
            <v-radio label="Use existing region" :value="true"></v-radio>
            <v-radio label="Create new region" :value="false"></v-radio>
          </v-radio-group>
          
          <template v-if="bulkUseExistingRegion">
            <v-select
              label="Region"
              v-model="bulkImportRegion"
              :items="regionOptions"
              item-text="text"
              item-value="value"
              placeholder="Select target region"
              class="mb-4"
            />
          </template>
          
          <template v-else>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="City"
                  v-model="bulkNewRegion.city"
                  placeholder="Enter city"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="Province"
                  v-model="bulkNewRegion.province"
                  placeholder="Enter province code (e.g. BC, ON)"
                  @input="bulkNewRegion.province = $event ? $event.toUpperCase() : ''"
                />
              </v-col>
            </v-row>
            <p class="text-caption grey--text mb-4">Area will be set to 'local' by default</p>
          </template>
          
          <v-textarea
            v-model="bulkImportPostcodes"
            label="Postcodes"
            placeholder="Enter postcodes separated by commas or newlines (e.g. L1S, L1T, L1V or one per line)"
            rows="6"
            auto-grow
            outlined
            counter
            hint="Format: postcodes separated by commas or newlines"
            persistent-hint
          />

          <v-alert
            v-if="bulkImportResults.length > 0"
            dense
            tile
            color="info"
            class="mt-4"
          >
            <div>Import results:</div>
            <div class="mt-1">
              <v-chip color="success" small class="mr-2">
                Success: {{ bulkImportResults.filter(r => r.success).length }}
              </v-chip>
              <v-chip color="error" small>
                Failed: {{ bulkImportResults.filter(r => !r.success).length }}
              </v-chip>
            </div>
            <v-expansion-panels v-if="bulkImportResults.filter(r => !r.success).length > 0" class="mt-2" flat>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  Show failed postcodes
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div v-for="(result, index) in bulkImportResults.filter(r => !r.success)" :key="index">
                    <v-chip color="error" x-small class="my-1">{{ result.postcode }}</v-chip>
                    <span class="ml-2">{{ result.error }}</span>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="showBulkImport = false">Close</v-btn>
          <v-spacer/>
          <v-btn 
            color="success" 
            @click="bulkImportPostcodesMethod" 
            :disabled="!isValidBulkImport"
            :loading="bulkImportLoading"
          >
            Import
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-tabs v-model="tabs" centered>
      <v-tab>Postcodes</v-tab>
      <v-tab>Excluded Postcodes</v-tab>
      <v-tab>Regions</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tabs">
      <!-- Postcodes Tab -->
      <v-tab-item>
        <v-card-title>
          Postcodes
          <v-spacer/>
          <DebouncedTextField
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            class="mx-4"
            clearable
          />
          <v-btn outlined color="primary" @click="openAddPostcode">Add Postcode</v-btn>
          <v-btn outlined color="success" class="ml-2" @click="showBulkImport = true">Bulk Import</v-btn>
          <v-btn outlined color="info" class="ml-2" @click="showPostcodeCheck = true">Check Postcode</v-btn>
          <v-btn outlined color="secondary" class="ml-2" @click="exportPostcodesCsv" :disabled="!filteredPostcodes.length">Export CSV</v-btn>
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="postcodeHeaders"
            :items="filteredPostcodes"
            :search="search"
            :loading="loading"
            class="elevation-1"
          >
            <template v-slot:item.actions="{ item }">
              <v-btn 
                small 
                icon 
                color="error" 
                @click="confirmRemovePostcode(item.postcode, item.region)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-tab-item>

      <!-- Excluded Postcodes Tab -->
      <v-tab-item>
        <v-card-title>
          Excluded Postcodes
          <v-spacer/>
          <v-text-field
            v-model="excludedSearch"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            class="mx-4"
            clearable
          />
          <v-dialog max-width="500px" v-model="showAddExcluded">
            <template v-slot:activator="{ on, attrs }">
              <v-btn 
                outlined 
                color="primary" 
                v-bind="attrs"
                v-on="on"
              >
                Add Excluded Postcode
              </v-btn>
            </template>
            <v-card>
              <v-card-title>Add Excluded Postcode</v-card-title>
              <v-card-text>
                <v-text-field
                  v-model="newExcludedPostcode"
                  label="Postcode"
                  placeholder="Enter postcode to exclude"
                />
              </v-card-text>
              <v-card-actions>
                <v-btn @click="showAddExcluded = false">Cancel</v-btn>
                <v-spacer/>
                <v-btn 
                  color="error" 
                  @click="addExcludedPostcode" 
                  :disabled="!newExcludedPostcode"
                >
                  Add to Exclusion List
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="excludedHeaders"
            :items="filteredExcludedPostcodes"
            :search="excludedSearch"
            :loading="loading"
            class="elevation-1"
          >
            <template v-slot:item.actions="{ item }">
              <v-btn 
                small 
                icon 
                color="success" 
                @click="confirmRemoveExcluded(item.postcode)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-tab-item>

      <!-- Regions Tab -->
      <v-tab-item>
        <v-card-title>
          Regions
          <v-spacer/>
          <v-text-field
            v-model="regionSearch"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
          />
        </v-card-title>
        <v-card-text>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Area</th>
                  <th>City</th>
                  <th>Province</th>
                  <th>Region ID</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="region in filteredRegions" :key="region.id">
                  <td>{{ region.area }}</td>
                  <td>{{ region.city }}</td>
                  <td>{{ region.province }}</td>
                  <td>{{ region.id }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>

<script>
import api from "@/api";
import urlState from "@/router/urlState";
import DebouncedTextField from "@/components/DebouncedTextField.vue";

export default {
  name: "Postcodes",
  components: {DebouncedTextField},
  mixins: [urlState],
  data() {
    return {
      tabs: 0,
      postcodes: [],
      excludedPostcodes: [],
      regions: [],
      loading: false,
      search: "",
      excludedSearch: "",
      regionSearch: "",
      showAddPostcode: false,
      showAddExcluded: false,
      showPostcodeCheck: false,
      showBulkImport: false,
      useExistingRegion: true,
      newPostcode: {
        postcode: "",
        region: ""
      },
      newRegion: {
        city: "",
        province: "",
        area: "local"
      },
      newExcludedPostcode: "",
      postcodeToCheck: "",
      postcodeCheckResult: null,
      bulkUseExistingRegion: true,
      bulkImportRegion: "",
      bulkImportPostcodes: "",
      bulkImportResults: [],
      bulkImportLoading: false,
      bulkNewRegion: {
        city: "",
        province: "",
        area: "local"
      },
      postcodeHeaders: [
        { text: "Postcode", value: "postcode" },
        { text: "Region", value: "region" },
        { text: "Actions", value: "actions", sortable: false }
      ],
      excludedHeaders: [
        { text: "Excluded Postcode", value: "postcode" },
        { text: "Actions", value: "actions", sortable: false }
      ]
    };
  },
  computed: {
    filteredPostcodes() {
      return this.postcodes || [];
    },
    filteredExcludedPostcodes() {
      return (this.excludedPostcodes || []).map(postcode => ({
        postcode
      }));
    },
    filteredRegions() {
      return Object.keys(this.regions || {}).map(id => ({
        id,
        ...this.parseRegion(id)
      }));
    },
    regionOptions() {
      return this.filteredRegions.map(region => ({
        text: `${region.area}, ${region.city}, ${region.province}`,
        value: region.id
      }));
    },
    isValidNewPostcode() {
      if (!this.newPostcode.postcode) return false;
      
      if (this.useExistingRegion) {
        return !!this.newPostcode.region;
      } else {
        return !!this.newRegion.city && !!this.newRegion.province;
      }
    },
    isValidBulkImport() {
      if (!this.bulkImportPostcodes || this.bulkImportPostcodes.trim().length === 0) {
        return false;
      }
      
      if (this.bulkUseExistingRegion) {
        return !!this.bulkImportRegion;
      } else {
        return !!this.bulkNewRegion.city && !!this.bulkNewRegion.province;
      }
    }
  },
  mounted() {
    this.syncToUrl({
      param: "tabs",
      urlParam: "t",
      initFromRoute: true
    });
    this.syncToUrl({
      param: "search",
      urlParam: "postcode",
      initFromRoute: true
    });

    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const { data } = await api.get("v2/postcodes");
        
        // Extract regions and postcode mappings from the nested structure
        this.regions = {};
        this.postcodes = [];
        this.excludedPostcodes = data.excludedPostcodes || [];
        
        // Process the nested postcode data structure
        if (data.postcodes && data.postcodes.regions) {
          // First, collect all region IDs
          Object.keys(data.postcodes.regions).forEach(regionId => {
            this.regions[regionId] = true;
            
            // For each region, extract postcodes
            const regionPostcodes = data.postcodes.regions[regionId];
            Object.keys(regionPostcodes).forEach(postcode => {
              if (regionPostcodes[postcode]) {
                this.postcodes.push({
                  postcode: postcode,
                  region: regionId
                });
              }
            });
          });
        }
      } catch (error) {
        console.error("Error fetching postcode data:", error);
        this.$store.commit("showSnackbar", {
          message: "Failed to load postcode data",
          color: "error"
        });
      } finally {
        this.loading = false;
      }
    },
    parseRegion(regionString) {
      const parts = regionString.split(":");
      return {
        area: parts[0] || "",
        city: parts[1] || "",
        province: parts[2] || ""
      };
    },
    openAddPostcode() {
      this.useExistingRegion = true;
      this.newPostcode = {
        postcode: "",
        region: ""
      };
      this.newRegion = {
        city: "",
        province: "",
        area: "local"
      };
      this.showAddPostcode = true;
    },
    createRegionId(region) {
      // Ensure province is always uppercase
      const province = region.province ? region.province.toUpperCase() : '';
      return `${region.area}:${region.city}:${province}`;
    },
    
    async addPostcode() {
      if (!this.isValidNewPostcode) return;
      
      try {
        let regionId;
        
        if (!this.useExistingRegion) {
          // Create new region ID string
          regionId = this.createRegionId(this.newRegion);
        } else {
          regionId = this.newPostcode.region;
        }
        
        // Use the determined region ID
        await api.post("v2/postcodes", {
          postcode: this.newPostcode.postcode,
          region: regionId
        });
        
        this.showAddPostcode = false;
        this.$store.commit("showSnackbar", {
          message: `Postcode ${this.newPostcode.postcode} added to region successfully`,
          color: "success"
        });
        this.fetchData();
      } catch (error) {
        console.error("Error adding postcode:", error);
        this.$store.commit("showSnackbar", {
          message: "Failed to add postcode",
          color: "error"
        });
      }
    },
    async confirmRemovePostcode(postcode, region) {
      if (!confirm(`Are you sure you want to remove ${postcode} from region ${region}?`)) {
        return;
      }
      
      try {
        // API expects the full URL format
        await api.delete(`v2/postcodes/${postcode}/region/${encodeURIComponent(region)}`);
        this.$store.commit("showSnackbar", {
          message: `Postcode ${postcode} removed from region successfully`,
          color: "success"
        });
        this.fetchData();
      } catch (error) {
        console.error("Error removing postcode:", error);
        this.$store.commit("showSnackbar", {
          message: "Failed to remove postcode",
          color: "error"
        });
      }
    },
    async addExcludedPostcode() {
      if (!this.newExcludedPostcode) return;
      
      try {
        await api.post("v2/postcodes/excluded", {
          postcode: this.newExcludedPostcode
        });
        this.showAddExcluded = false;
        this.newExcludedPostcode = "";
        this.$store.commit("showSnackbar", {
          message: "Postcode added to exclusion list successfully",
          color: "success"
        });
        this.fetchData();
      } catch (error) {
        console.error("Error adding excluded postcode:", error);
        this.$store.commit("showSnackbar", {
          message: "Failed to add postcode to exclusion list",
          color: "error"
        });
      }
    },
    async confirmRemoveExcluded(postcode) {
      if (!confirm(`Are you sure you want to remove ${postcode} from the exclusion list?`)) {
        return;
      }
      
      try {
        await api.delete(`v2/postcodes/excluded/${postcode}`);
        this.$store.commit("showSnackbar", {
          message: `Postcode ${postcode} removed from exclusion list successfully`,
          color: "success"
        });
        this.fetchData();
      } catch (error) {
        console.error("Error removing excluded postcode:", error);
        this.$store.commit("showSnackbar", {
          message: "Failed to remove postcode from exclusion list",
          color: "error"
        });
      }
    },
    async checkPostcode() {
      if (!this.postcodeToCheck) return;
      
      try {
        const { data } = await api.get(`v2/postcodes/check/${this.postcodeToCheck}`);
        this.postcodeCheckResult = data;
        this.postcodeCheckResult.valid = !data.error;
      } catch (error) {
        console.error("Error checking postcode:", error);
        this.$store.commit("showSnackbar", {
          message: "Failed to check postcode",
          color: "error"
        });
        this.postcodeCheckResult = {
          postcode: this.postcodeToCheck,
          valid: false
        };
      }
    },
    
    async bulkImportPostcodesMethod() {
      if (!this.isValidBulkImport) return;
      
      this.bulkImportLoading = true;
      this.bulkImportResults = [];
      
      // Parse the input into an array, supporting both commas and newlines as separators
      const postcodes = this.bulkImportPostcodes
        // Replace commas with newlines, then split by newlines
        .replace(/,/g, '\n')
        .split('\n')
        .map(p => p.trim())
        .filter(p => p.length > 0);
      
      if (postcodes.length === 0) {
        this.bulkImportLoading = false;
        return;
      }
      
      // Determine the region ID to use
      let regionId;
      if (!this.bulkUseExistingRegion) {
        // Create new region ID string
        regionId = this.createRegionId(this.bulkNewRegion);
      } else {
        regionId = this.bulkImportRegion;
      }
      
      let successCount = 0;
      const promises = postcodes.map(async (postcode) => {
        try {
          await api.post("v2/postcodes", {
            postcode: postcode,
            region: regionId
          });
          successCount++;
          return { postcode, success: true };
        } catch (error) {
          console.error(`Error adding postcode ${postcode}:`, error);
          let errorMessage = 'Failed to add postcode';
          if (error.response && error.response.data && error.response.data.error) {
            errorMessage = error.response.data.error;
          }
          return { 
            postcode, 
            success: false, 
            error: errorMessage
          };
        }
      });
      
      // Wait for all promises to resolve
      this.bulkImportResults = await Promise.all(promises);
      
      // Show a summary notification with region information
      let regionDisplay = regionId;
      this.$store.commit("showSnackbar", {
        message: `Bulk import complete: ${successCount} of ${postcodes.length} postcodes added to region '${regionDisplay}' successfully`,
        color: successCount > 0 ? "success" : "warning",
        timeout: 5000
      });
      
      // Refresh the data if any postcodes were successfully added
      if (successCount > 0) {
        await this.fetchData();
      }
      
      this.bulkImportLoading = false;
    },

    exportPostcodesCsv() {
      const escape = (val) => {
        const str = val == null ? "" : String(val);
        return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
      };

      const headers = ["postcode", "region", "area", "city", "province"];
      const rows = this.filteredPostcodes.map((item) => {
        const { area, city, province } = this.parseRegion(item.region);
        return [item.postcode, item.region, area, city, province].map(escape).join(",");
      });
      const csv = [headers.join(","), ...rows].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const date = new Date().toISOString().slice(0, 10);
      const link = document.createElement("a");
      link.href = url;
      link.download = `postcodes-${date}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  },
  props: {
    readOnly: { type: Boolean, default: false }
  }
};
</script>

<style scoped>
.v-data-table {
  margin-top: 1rem;
}
</style>